<template>
  <div class="thc-card">

    <!-- Header -->
    <div class="thc-header">
      <div class="thc-header-left">
        <i class="ti ti-sparkles" aria-hidden="true"></i>
        <span>Headcount demand forecast</span>
      </div>
      <span class="thc-sub">Next 6 months · Live data</span>
    </div>

    <!-- Summary metrics -->
    <div class="thc-metrics">
      <div class="thc-metric">
        <div class="thc-metric-val"
             :style="{ color: data.summary.totalNeed90d > 0 ? '#A32D2D' : '#3B6D11' }">
          {{ data.summary.totalNeed90d }}
        </div>
        <div class="thc-metric-label">Hires needed (90d)</div>
      </div>
      <div class="thc-metric">
        <div class="thc-metric-val"
             :style="{ color: data.summary.totalNeed180d > 0 ? '#854F0B' : '#3B6D11' }">
          {{ data.summary.totalNeed180d }}
        </div>
        <div class="thc-metric-label">Hires needed (180d)</div>
      </div>
      <div class="thc-metric">
        <div class="thc-metric-val"
             :style="{ color: data.summary.urgentDepts > 0 ? '#A32D2D' : 'inherit' }">
          {{ data.summary.urgentDepts }}
        </div>
        <div class="thc-metric-label">Urgent depts</div>
      </div>
      <div class="thc-metric">
        <div class="thc-metric-val">{{ data.summary.totalExiting }}</div>
        <div class="thc-metric-label">Confirmed exits</div>
      </div>
    </div>

    <!-- Department forecast rows -->
    <div class="thc-section-title">By department</div>
    <div class="thc-list">
      <div
        v-for="(dept, i) in topDepts"
        :key="i"
        class="thc-row"
      >
        <div class="thc-dept-info">
          <div class="thc-dept-name">{{ dept.deptname }}</div>
          <div class="thc-dept-sub">
            {{ dept.current_headcount }} staff ·
            avg {{ dept.avg_days_to_fill }}d to fill
          </div>
        </div>
        <div class="thc-need">
          <span
            v-if="dept.total_need_90d > 0"
            class="thc-need-num"
            :style="{ color: '#A32D2D' }"
          >+{{ dept.total_need_90d }}</span>
          <span v-else class="thc-need-num" style="color:#3B6D11">✓</span>
          <div class="thc-need-label">90d</div>
        </div>
        <div class="thc-need">
          <span
            class="thc-need-num"
            :style="{ color: dept.total_need_180d > 0 ? '#854F0B' : '#3B6D11' }"
          >{{ dept.total_need_180d > 0 ? '+' + dept.total_need_180d : '✓' }}</span>
          <div class="thc-need-label">180d</div>
        </div>
        <span class="thc-badge" :class="urgencyClass(dept.urgency_flag)">
          {{ dept.urgency_flag }}
        </span>
        <div class="thc-recruit-date" v-if="dept.recruit_start_date_90d">
          <div class="thc-date-label">Start recruiting</div>
          <div class="thc-date-val">{{ formatDate(dept.recruit_start_date_90d) }}</div>
        </div>
      </div>

      <div v-if="data.forecast.length === 0" class="thc-empty">
        No hiring needs forecasted in the next 180 days.
        All departments are stable.
      </div>
    </div>

    <!-- Exiting staff (if any) -->
    <div v-if="data.exitingStaff && data.exitingStaff.length > 0">
      <div class="thc-section-title" style="margin-top:8px">Confirmed exits</div>
      <div class="thc-exit-list">
        <div
          v-for="(staff, i) in data.exitingStaff.slice(0, 5)"
          :key="'e' + i"
          class="thc-exit-row"
        >
          <div class="thc-exit-avatar">{{ initials(staff.psnname) }}</div>
          <div class="thc-exit-info">
            <div class="thc-exit-name">{{ staff.psnname }}</div>
            <div class="thc-exit-sub">{{ staff.deptname }} · {{ staff.exit_reason }}</div>
          </div>
          <div class="thc-exit-days">
            <div class="thc-days-num" :style="{ color: staff.days_remaining <= 30 ? '#A32D2D' : '#854F0B' }">
              {{ staff.days_remaining }}d
            </div>
            <div class="thc-days-label">remaining</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer actions -->
    <div class="thc-footer">
      <button class="thc-btn" @click="askMore('Show me the full headcount forecast for all departments')">
        Full forecast ↗
      </button>
      <button class="thc-btn" @click="askMore('Which department should I start recruiting for first?')">
        Where to start ↗
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: "TaraHeadcountCard",
  props: {
    data: { type: Object, required: true },
  },
  computed: {
    topDepts() {
      return (this.data.forecast || []).slice(0, 6);
    },
  },
  methods: {
    urgencyClass(flag) {
      if (flag === 'URGENT')  return 'thc-badge-urgent';
      if (flag === 'PLAN')    return 'thc-badge-plan';
      if (flag === 'MONITOR') return 'thc-badge-monitor';
      return 'thc-badge-stable';
    },
    formatDate(date) {
      if (!date) return '';
      const d = new Date(date);
      return d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    initials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      return parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
    },
    askMore(text) {
      this.$emit('ask', text);
    },
  },
};
</script>

<style scoped>
.thc-card {
  border: 0.5px solid #AFA9EC;
  border-radius: 12px;
  background: #FAFAFE;
  overflow: hidden;
  font-family: inherit;
  font-size: 13px;
  margin: 4px 0 8px;
  max-width: 440px;
}
.thc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px 8px;
  background: #EEEDFE;
  border-bottom: 0.5px solid #AFA9EC;
}
.thc-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
  color: #3C3489;
  font-size: 13px;
}
.thc-header-left i { font-size: 15px; }
.thc-sub { font-size: 11px; color: #534AB7; }
.thc-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  border-bottom: 0.5px solid #E5E3F8;
}
.thc-metric {
  padding: 10px 8px;
  text-align: center;
  border-right: 0.5px solid #E5E3F8;
}
.thc-metric:last-child { border-right: none; }
.thc-metric-val { font-size: 20px; font-weight: 500; color: #26215C; line-height: 1.2; }
.thc-metric-label { font-size: 10px; color: #7F77DD; margin-top: 2px; }
.thc-section-title {
  font-size: 11px;
  font-weight: 500;
  color: #7F77DD;
  padding: 8px 14px 4px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.thc-list { padding: 0 0 4px; }
.thc-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  border-bottom: 0.5px solid #F0EFF9;
}
.thc-row:last-child { border-bottom: none; }
.thc-dept-info { flex: 1; min-width: 0; }
.thc-dept-name {
  font-size: 12px;
  font-weight: 500;
  color: #26215C;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.thc-dept-sub { font-size: 10px; color: #7F77DD; margin-top: 1px; }
.thc-need { text-align: center; min-width: 32px; }
.thc-need-num { font-size: 14px; font-weight: 500; display: block; }
.thc-need-label { font-size: 9px; color: #7F77DD; }
.thc-badge {
  font-size: 10px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 20px;
  flex-shrink: 0;
  white-space: nowrap;
}
.thc-badge-urgent  { background: #FCEBEB; color: #791F1F; }
.thc-badge-plan    { background: #FAEEDA; color: #633806; }
.thc-badge-monitor { background: #E6F1FB; color: #0C447C; }
.thc-badge-stable  { background: #EAF3DE; color: #27500A; }
.thc-recruit-date { text-align: right; min-width: 70px; }
.thc-date-label { font-size: 9px; color: #7F77DD; }
.thc-date-val { font-size: 11px; font-weight: 500; color: #26215C; }
.thc-empty {
  padding: 16px 14px;
  font-size: 12px;
  color: #7F77DD;
  text-align: center;
}
.thc-exit-list { padding: 0 0 4px; }
.thc-exit-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  border-bottom: 0.5px solid #F0EFF9;
}
.thc-exit-row:last-child { border-bottom: none; }
.thc-exit-avatar {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #FAEEDA;
  color: #633806;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 500;
  flex-shrink: 0;
}
.thc-exit-info { flex: 1; min-width: 0; }
.thc-exit-name { font-size: 12px; font-weight: 500; color: #26215C; }
.thc-exit-sub { font-size: 10px; color: #7F77DD; }
.thc-exit-days { text-align: center; min-width: 40px; }
.thc-days-num { font-size: 14px; font-weight: 500; display: block; }
.thc-days-label { font-size: 9px; color: #7F77DD; }
.thc-footer {
  display: flex;
  gap: 8px;
  padding: 10px 14px;
  background: #F5F4FD;
  border-top: 0.5px solid #E5E3F8;
}
.thc-btn {
  font-size: 12px;
  padding: 5px 10px;
  border-radius: 6px;
  border: 0.5px solid #AFA9EC;
  background: white;
  color: #3C3489;
  cursor: pointer;
}
.thc-btn:hover { background: #EEEDFE; }
</style>
